import { useEffect, useState, type ReactNode } from 'react';
import { useIsFetching, type UseQueryResult } from '@tanstack/react-query';
import { ApiError } from '@immoaktas/api-client';
import { apiErrorText } from '@immoaktas/i18n';
import { getI18n, useTranslation } from 'react-i18next';
import { Button } from './buttons';
import { Icon } from './icons';

/** True after `ms` (skeletons appear only after 150 ms so fast answers do not flicker, docs/10). */
export function useDelayedFlag(ms = 150) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setOn(true), ms);
    return () => clearTimeout(t);
  }, [ms]);
  return on;
}

/** Shimmer bars. Keeps the old API (h, w, count). */
export function Skeleton({ h = 18, w = '100%', count = 1, round }: { h?: number; w?: number | string; count?: number; round?: boolean }) {
  const show = useDelayedFlag();
  const { t } = useTranslation('common');
  if (!show) return null;
  return (
    <div className="stack tight" role="status" aria-busy="true" aria-label={t('ui.loadingLabel')}>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className={`skeleton ${round ? 'round' : ''}`} style={{ height: h, width: w }} />
      ))}
    </div>
  );
}

const widths = ['62%', '48%', '70%', '40%', '56%', '66%'];
/** Table-shaped skeleton (Skeleton · Tabelle): same columns as the real table, max 6 rows. */
export function SkeletonTable({ columns = 4, rows = 5, head = true, bare }: { columns?: number; rows?: number; head?: boolean; bare?: boolean }) {
  const show = useDelayedFlag();
  const { t } = useTranslation('common');
  if (!show) return null;
  const tpl = { gridTemplateColumns: `minmax(0, 2fr) repeat(${Math.max(1, columns - 1)}, minmax(0, 1fr))` };
  return (
    <div className={`sk-table ${bare ? 'bare' : ''}`} role="status" aria-busy="true" aria-label={t('ui.loadingLabel')}>
      {head && (
        <div className="sk-row sk-head" style={tpl}>
          {Array.from({ length: columns }, (_, c) => <span key={c} className="skeleton" style={{ height: 10, width: c === 0 ? '30%' : '50%' }} />)}
        </div>
      )}
      {Array.from({ length: Math.min(rows, 6) }, (_, r) => (
        <div key={r} className="sk-row" style={tpl}>
          <span className="sk-cell">
            <span className="skeleton" style={{ height: 12, width: widths[r % widths.length] }} />
            <span className="skeleton" style={{ height: 9, width: '40%' }} />
          </span>
          {Array.from({ length: columns - 1 }, (_, c) =>
            c === columns - 2 ? <span key={c} className="skeleton round" style={{ height: 20, width: 76 }} /> : <span key={c} className="skeleton" style={{ height: 12, width: widths[(r + c + 2) % widths.length] }} />,
          )}
        </div>
      ))}
    </div>
  );
}

/** Card-shaped skeleton (Skeleton · Karte). */
export function SkeletonCard({ lines = 2 }: { lines?: number }) {
  const show = useDelayedFlag();
  const { t } = useTranslation('common');
  if (!show) return null;
  return (
    <div className="sk-card" role="status" aria-busy="true" aria-label={t('ui.loadingLabel')}>
      <span style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <span className="skeleton" style={{ width: 52, height: 52, borderRadius: 12 }} />
        <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span className="skeleton" style={{ height: 14, width: '70%' }} />
          <span className="skeleton" style={{ height: 10, width: '45%' }} />
        </span>
      </span>
      {Array.from({ length: lines }, (_, i) => <span key={i} className="skeleton round" style={{ height: 8, width: i ? '60%' : '100%' }} />)}
    </div>
  );
}

/** KPI tiles skeleton. */
export function SkeletonKpis({ count = 4 }: { count?: number }) {
  const show = useDelayedFlag();
  const { t } = useTranslation('common');
  if (!show) return <div className="kpi-grid" aria-hidden style={{ minHeight: 118 }} />;
  return (
    <div className="kpi-grid" role="status" aria-busy="true" aria-label={t('ui.loadingLabel')}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="card kpi">
          <span className="skeleton" style={{ height: 10, width: '55%' }} />
          <span className="skeleton" style={{ height: 28, width: '45%', margin: '4px 0' }} />
          <span className="skeleton" style={{ height: 9, width: '70%' }} />
        </div>
      ))}
    </div>
  );
}

// ───────────── empty states (line illustrations from design/Zustaende) ─────────────
export type EmptyArt = 'receipt' | 'house' | 'statement' | 'list' | 'bell' | 'none';
function EmptyIllustration({ art }: { art: EmptyArt }) {
  if (art === 'none') return null;
  const common = { width: 120, height: 90, viewBox: '0 0 120 90', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true, className: 'empty-art' };
  if (art === 'receipt')
    return (
      <svg {...common}>
        <path d="M34 14 H70 L82 26 V78 H34 Z" className="art-fill" />
        <path d="M70 14 V26 H82" />
        <path d="M42 40 H72 M42 49 H72 M42 58 H60" className="art-accent" stroke="currentColor" />
        <path d="M60 70 H74" strokeWidth={3} />
        <circle cx="88" cy="66" r="12" className="art-tint" />
        <path d="M88 60 V72 M82 66 H94" />
        <path d="M18 30 l4 4 M22 30 l-4 4 M98 16 l3 3 M101 16 l-3 3" className="art-muted" />
      </svg>
    );
  if (art === 'statement')
    return (
      <svg {...common}>
        <path d="M30 12 H78 V80 H30 Z" className="art-fill" />
        <path d="M40 26 H68 M40 36 H68 M40 46 H58" className="art-accent" />
        <path d="M40 62 H68" strokeDasharray="3 4" />
        <path d="M84 30 L96 18 L102 24 L90 36 Z" className="art-tint" />
        <path d="M84 30 L82 38 L90 36" />
        <path d="M96 18 L102 24" />
      </svg>
    );
  if (art === 'list')
    return (
      <svg {...common}>
        <rect x="24" y="16" width="72" height="58" rx="8" className="art-fill" />
        <path d="M24 30 H96" />
        <path d="M34 42 H62 M34 52 H72 M34 62 H54" className="art-accent" />
        <circle cx="82" cy="52" r="3" className="art-muted" />
      </svg>
    );
  if (art === 'bell')
    return (
      <svg {...common}>
        <path d="M42 62 V44 a18 18 0 0 1 36 0 V62 L82 68 H38 Z" className="art-fill" />
        <path d="M54 76 H66" />
        <path d="M50 44 a10 10 0 0 1 10 -10" className="art-accent" />
        <path d="M22 30 l4 4 M26 30 l-4 4 M94 22 l3 3 M97 22 l-3 3" className="art-muted" />
      </svg>
    );
  return (
    <svg {...common}>
      <path d="M22 44 L60 14 L98 44 V80 H22 Z" className="art-fill" />
      <path d="M50 80 V58 H70 V80" />
      <circle cx="60" cy="36" r="6" className="art-accent" />
      <path d="M34 52 H44 V62 H34 Z M76 52 H86 V62 H76 Z" className="art-accent" />
      <path d="M8 80 H112" className="art-line" />
    </svg>
  );
}

/** Empty state: one sentence what goes here and exactly one primary action; optional secondary link. */
export const EmptyState = ({ title, text, action, secondary, art = 'house', spacious }: { title: string; text?: ReactNode; action?: ReactNode; secondary?: ReactNode; art?: EmptyArt; spacious?: boolean }) => (
  <div className={`empty ${spacious ? 'spacious' : ''}`}>
    <EmptyIllustration art={art} />
    <h3 className="empty-title">{title}</h3>
    {text && <p className="empty-text">{text}</p>}
    {(action || secondary) && (
      <div className="empty-actions">
        {action}
        {secondary}
      </div>
    )}
  </div>
);

// ───────────── errors ─────────────
/** Minimal translate signature (i18next `t`); keeps errorText usable outside React. */
export type Translate = (key: string, opts?: Record<string, unknown>) => string;
const globalT: Translate = (key, opts) => {
  const i = getI18n();
  return i ? String(i.t(key, opts)) : key;
};
/**
 * User-facing, translated message for any error. Never shows server sentences (B8): API errors map their stable
 * key (errors:server.<key>) or code via apiErrorText(); unknown codes and non-API errors fall back to errors:generic.
 */
export function errorText(e: unknown, t: Translate = globalT): string {
  const generic = t('errors:generic');
  if (e instanceof ApiError) return e.code === 'NETWORK' ? t('errors:network') : apiErrorText(t, e);
  return generic;
}

/** Error with retry (Fehler mit Wiederholen): what failed, nothing is lost, retry; code folded under Details. */
export function ErrorState({ error, onRetry, title, retrying }: { error: unknown; onRetry?: () => void; title?: string; retrying?: boolean }) {
  const { t } = useTranslation('common');
  const offline = error instanceof ApiError && error.code === 'NETWORK';
  return (
    <div className="error-state" role="alert">
      <p className="error-title"><Icon name="alert" size={22} strokeWidth={2} />{title ?? (offline ? t('ui.error.offlineTitle') : t('ui.error.title'))}</p>
      <p className="error-text">{errorText(error, t)} {t('ui.error.safe')}</p>
      <div className="error-actions">
        {onRetry && <Button variant="danger" size="md" loading={retrying} loadingLabel={t('ui.retrying')} onClick={onRetry}>{t('retry')}</Button>}
        {error instanceof ApiError && (
          <details>
            <summary>{t('ui.error.details')}</summary>
            <span>{t('ui.error.code', { code: error.status ? `${error.status} · ${error.code}` : error.code })}</span>
          </details>
        )}
      </div>
    </div>
  );
}

/** Renders loading / error / empty / data for a query. */
export function Q<T>({ q, children, skeleton, empty, errorTitle }: { q: UseQueryResult<T>; children: (d: T) => ReactNode; skeleton?: ReactNode; empty?: (d: T) => ReactNode | null; errorTitle?: string }) {
  if (q.isPending) return <>{skeleton ?? <Skeleton count={4} h={22} />}</>;
  if (q.isError) return <ErrorState error={q.error} title={errorTitle} retrying={q.isFetching} onRetry={() => void q.refetch()} />;
  const e = empty?.(q.data);
  if (e) return <>{e}</>;
  return <>{children(q.data)}</>;
}

/** Thin indeterminate bar at the top while anything refetches (Nachladen). */
export function GlobalFetching() {
  const n = useIsFetching();
  return n > 0 ? <div className="topbar-progress" aria-hidden /> : null;
}

/** Offline banner on top of the page; after reconnecting a green confirmation for 3 s (design/Zustaende). */
export function NetworkBanner() {
  const { t } = useTranslation('common');
  const [state, setState] = useState<'online' | 'offline' | 'back'>(() => (typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'online'));
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = () => { clearTimeout(timer); setState('offline'); };
    const on = () => { setState((s) => (s === 'offline' ? 'back' : s)); timer = setTimeout(() => setState('online'), 3000); };
    window.addEventListener('offline', off);
    window.addEventListener('online', on);
    return () => { window.removeEventListener('offline', off); window.removeEventListener('online', on); clearTimeout(timer); };
  }, []);
  if (state === 'online') return null;
  return (
    <div className={`net-banner ${state === 'offline' ? 'offline' : 'online'}`} role="status">
      <span className="dot" aria-hidden />
      <span style={{ flex: 1 }}>{state === 'offline' ? t('ui.offline.banner') : t('ui.offline.back')}</span>
    </div>
  );
}
